<template>
  <section class="admin-content" id="telemedicine-network-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-users" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Virtual Practice Network</h4>
                <small style="opacity: .8;">Invite doctors to register for telemedicine and review their applications</small>
              </div>
            </div>
            <router-link to="/telemedicine" class="btn btn-light btn-sm"><i class="fe fe-video mr-1"></i>Consultations</router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <ul class="nav nav-pills mb-3" role="tablist">
            <li class="nav-item">
              <button type="button" class="nav-link" :class="{ active: tab === 'applications' }" role="tab"
                :aria-selected="tab === 'applications'" @click="tab = 'applications'">
                <i class="fe fe-inbox mr-1"></i>Applications
                <span v-if="pendingCount" class="badge badge-warning ml-1">{{ pendingCount }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button type="button" class="nav-link" :class="{ active: tab === 'links' }" role="tab"
                :aria-selected="tab === 'links'" @click="tab = 'links'">
                <i class="fe fe-link mr-1"></i>Invitation links
              </button>
            </li>
          </ul>

          <!-- Applications -->
          <div v-if="tab === 'applications'">
            <div class="d-flex align-items-center mb-3" style="gap: 8px;">
              <label for="tn-status" class="small font-weight-bold mb-0">Status</label>
              <select id="tn-status" v-model="status" class="form-control form-control-sm" style="max-width: 180px;" @change="loadRegistrations">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div class="app-card mb-3" v-for="r in registrations" :key="r.id">
              <div class="d-flex justify-content-between flex-wrap" style="gap: 12px;">
                <div>
                  <h6 class="mb-1 font-weight-bold">
                    {{ r.provider ? r.provider.agency_name : 'Doctor #' + r.provider_id }}
                    <span class="status-pill ml-2" :class="'status-pill--' + r.status">{{ r.status }}</span>
                  </h6>
                  <div class="small text-muted" v-if="r.provider">
                    {{ r.provider.email }} · {{ r.provider.phone_number }}
                  </div>
                  <div class="small mt-1">
                    <strong>MDCN:</strong> {{ r.license_number || '—' }}
                    <span v-if="r.years_of_experience !== null"> · {{ r.years_of_experience }} years of practice</span>
                    <span class="text-muted"> · registered {{ r.created_at }}</span>
                    <span class="text-muted" v-if="r.invite && r.invite.title"> via "{{ r.invite.title }}"</span>
                  </div>
                  <div class="mt-2">
                    <span v-for="s in r.specializations" :key="s.id" class="spec-chip">{{ s.name }}</span>
                  </div>
                  <p class="small mt-2 mb-1" v-if="r.bio" style="white-space: pre-line;">{{ r.bio }}</p>
                  <div class="small mt-2">
                    <strong>Qualifications:</strong>
                    <span v-if="!r.documents.length" class="text-muted">none uploaded</span>
                    <a v-for="d in r.documents" :key="d.id" :href="d.url" target="_blank" rel="noopener" class="doc-link">
                      <i class="fe fe-file-text mr-1"></i>{{ d.name }}
                    </a>
                  </div>
                  <div class="small text-muted mt-1" v-if="r.reason">Note: {{ r.reason }}</div>
                </div>
                <div class="text-nowrap" v-if="canEdit">
                  <button v-if="r.status !== 'approved'" type="button" class="btn btn-sm btn-success mr-1" @click="decide(r, 'approved')">
                    <i class="fe fe-check mr-1"></i>Approve
                  </button>
                  <button v-if="r.status !== 'rejected'" type="button" class="btn btn-sm btn-outline-danger" @click="decide(r, 'rejected')">
                    {{ r.status === 'approved' ? 'Suspend' : 'Reject' }}
                  </button>
                  <div class="mt-2">
                    <router-link :to="'/provider-' + r.provider_id" class="small">Facility profile</router-link>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!registrations.length" class="text-center text-muted py-5 app-card">
              <i class="fe fe-inbox" style="font-size: 2rem; opacity: .3;"></i>
              <p class="mt-2 mb-0">No applications yet. Share an invitation link with doctors.</p>
            </div>
          </div>

          <!-- Invitation links -->
          <div v-if="tab === 'links'" class="row">
            <div class="col-lg-7 mb-4">
              <div class="app-card mb-3" v-for="inv in invites" :key="inv.id">
                <div class="d-flex justify-content-between flex-wrap" style="gap: 8px;">
                  <div style="min-width: 0;">
                    <h6 class="mb-1 font-weight-bold">{{ inv.title || 'Invitation link' }}
                      <span class="status-pill ml-2" :class="inv.is_active ? 'status-pill--approved' : 'status-pill--rejected'">{{ inv.is_active ? 'open' : 'closed' }}</span>
                    </h6>
                    <div class="small text-muted">
                      {{ inv.registrations_count }} registration(s)<span v-if="inv.max_registrations"> of {{ inv.max_registrations }}</span>
                      <span v-if="inv.expires_at"> · expires {{ inv.expires_at }}</span>
                    </div>
                    <div class="link-box mt-2">
                      <code class="text-truncate">{{ joinUrl(inv) }}</code>
                      <button type="button" class="btn btn-sm btn-light" @click="copy(inv)"><i class="fe fe-copy mr-1"></i>Copy</button>
                      <a :href="joinUrl(inv)" target="_blank" rel="noopener" class="btn btn-sm btn-light"><i class="fe fe-external-link"></i><span class="sr-only">Open</span></a>
                    </div>
                  </div>
                  <div v-if="canEdit">
                    <button type="button" class="btn btn-sm" :class="inv.is_active ? 'btn-outline-danger' : 'btn-outline-success'" @click="toggle(inv)">
                      {{ inv.is_active ? 'Close link' : 'Reopen' }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="!invites.length" class="text-center text-muted py-5 app-card">No invitation links yet.</div>
            </div>

            <div class="col-lg-5 mb-4" v-if="canEdit">
              <form class="app-card" @submit.prevent="createInvite">
                <h6 class="font-weight-bold mb-3"><i class="fe fe-plus mr-1"></i>New invitation link</h6>
                <div class="form-group">
                  <label for="inv-title" class="small font-weight-bold">Label (only you see it)</label>
                  <input id="inv-title" v-model="inviteForm.title" class="form-control" placeholder="e.g. Paediatricians, Q4 outreach" />
                </div>
                <div class="form-group">
                  <label for="inv-msg" class="small font-weight-bold">Message to doctors (shown on the page)</label>
                  <textarea id="inv-msg" v-model="inviteForm.message" rows="4" class="form-control"
                    placeholder="Why join, what you expect, whether consultations are paid or volunteer…"></textarea>
                </div>
                <div class="form-row">
                  <div class="form-group col-6">
                    <label for="inv-exp" class="small font-weight-bold">Expires</label>
                    <input id="inv-exp" v-model="inviteForm.expires_at" type="date" class="form-control" :min="today" />
                  </div>
                  <div class="form-group col-6">
                    <label for="inv-max" class="small font-weight-bold">Max registrations</label>
                    <input id="inv-max" v-model.number="inviteForm.max_registrations" type="number" min="1" class="form-control" placeholder="No limit" />
                  </div>
                </div>
                <button type="submit" class="btn btn-submit btn-block">Create link</button>
                <p class="small text-muted mt-2 mb-0">The page shows your agency's logo and name: "{{ agencyName }} is inviting you to join our Virtual Practice Network".</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

function stored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    return null;
  }
}

export default {
  components: { Navbar },
  data() {
    return {
      agency: stored("agency") || {},
      tab: "applications",
      status: "pending",
      registrations: [],
      pendingCount: 0,
      invites: [],
      canEdit: false,
      inviteForm: { title: "", message: "", expires_at: "", max_registrations: "" },
      today: new Date().toISOString().slice(0, 10),
    };
  },
  computed: {
    agencyName() {
      return this.agency.agency_name || this.agency.firstname || "Your agency";
    },
  },
  created() {
    this.loadRegistrations();
    this.loadInvites();
  },
  methods: {
    notify(message) {
      this.$toasted.show(message, { position: "top-center", duration: 3000 });
    },
    errorText(err, fallback) {
      const d = (err.response && err.response.data) || {};
      return d.errors ? Object.values(d.errors).flat().join(" ") : d.message || fallback;
    },
    joinUrl(inv) {
      return `${window.location.origin}/join-telemedicine/${inv.token}`;
    },
    loadRegistrations() {
      const params = { per_page: 200 };
      if (this.status) params.status = this.status;
      this.axios.get("/api/v1/auth/telemedicine/registrations", { params }).then((res) => {
        this.registrations = res.data.data || [];
        if (this.status === "pending") this.pendingCount = res.data.total || 0;
      });
    },
    loadInvites() {
      this.axios.get("/api/v1/auth/telemedicine/invites").then((res) => {
        this.invites = res.data.data || [];
        this.canEdit = res.data.can_edit;
      });
    },
    createInvite() {
      const payload = {};
      Object.entries(this.inviteForm).forEach(([k, v]) => {
        if (v !== "" && v !== null) payload[k] = k === "expires_at" ? `${v} 23:59:59` : v;
      });
      this.axios
        .post("/api/v1/auth/telemedicine/invites", payload)
        .then((res) => {
          this.inviteForm = { title: "", message: "", expires_at: "", max_registrations: "" };
          this.loadInvites();
          this.copy(res.data.data);
        })
        .catch((err) => this.$toasted.error(this.errorText(err, "Could not create the link"), { position: "top-center", duration: 4000 }));
    },
    toggle(inv) {
      this.axios
        .put(`/api/v1/auth/telemedicine/invites/${inv.id}`, { is_active: !inv.is_active })
        .then(() => this.loadInvites())
        .catch((err) => this.$toasted.error(this.errorText(err, "Could not update the link"), { position: "top-center", duration: 4000 }));
    },
    copy(inv) {
      const url = this.joinUrl(inv);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => this.notify("Link copied, share it with doctors"));
      } else {
        window.prompt("Copy this link:", url);
      }
    },
    decide(r, status) {
      let reason = null;
      if (status === "rejected") {
        reason = window.prompt("Reason (kept with the application):", "");
        if (reason === null) return;
      } else if (!window.confirm(`Approve ${r.provider ? r.provider.agency_name : "this doctor"}? Patients can then be sent to them.`)) {
        return;
      }
      this.axios
        .post(`/api/v1/auth/telemedicine/registrations/${r.id}`, { status, reason })
        .then((res) => {
          this.notify(res.data.message || "Saved");
          this.loadRegistrations();
          if (this.status !== "pending") {
            this.axios.get("/api/v1/auth/telemedicine/registrations", { params: { status: "pending", per_page: 1 } })
              .then((p) => (this.pendingCount = p.data.total || 0));
          }
        })
        .catch((err) => this.$toasted.error(this.errorText(err, "Could not save"), { position: "top-center", duration: 4000 }));
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.app-card { background: #fff; border-radius: 12px; padding: 18px; box-shadow: 0 1px 6px rgba(0,0,0,.06); }
.nav-pills .nav-link { border: none; background: #fff; margin-right: 6px; color: #334155; border-radius: 8px; font-size: .88rem; }
.nav-pills .nav-link.active { background: #4c1d95; color: #fff; }
.spec-chip { display: inline-block; background: #ede9fe; color: #5b21b6; border-radius: 20px; padding: 2px 10px; font-size: .76rem; font-weight: 600; margin: 0 6px 6px 0; }
.status-pill { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: .72rem; font-weight: 600; text-transform: capitalize; }
.status-pill--approved { background: #d1fae5; color: #065f46; }
.status-pill--pending { background: #fef3c7; color: #92400e; }
.status-pill--rejected { background: #fee2e2; color: #991b1b; }
.doc-link { display: inline-block; margin-left: 8px; }
.link-box { display: flex; align-items: center; gap: 6px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 4px 6px 4px 10px; max-width: 100%; }
.link-box code { flex: 1; min-width: 0; font-size: .75rem; color: #4c1d95; }
.btn-submit { background: linear-gradient(135deg, #4c1d95, #7c3aed); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
</style>
